import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, UrlTree } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { Menu } from '@shared/models/menu.model';
import { AppStates } from '@shared/store/state/app.state';
import * as _ from 'lodash';
import { CookieService } from 'ngx-cookie-service';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

import { environment as env } from '../../../environments/environment';
import { ClientIdTypeEnum } from '../enum/client-id.enum';
import { selectAuthoritiesResult, selectUserInfoResult } from '../store/selectors/auth-user-info.selector';
import { AuthUserInfoState } from '../store/state/auth-user-info.state';
import { CookiesUtil } from '../utils/cookies-util';

type AuthenticationType = Observable<boolean> | Promise<boolean> | boolean | UrlTree;

@Injectable({
  providedIn: 'root'
})
export class AuthGuardService implements CanActivate, CanActivateChild {
  protected authUserInfo: AuthUserInfoState;
  private authorities: string[];

  constructor(
    public router: Router,
    private readonly store: Store<AppStates>,
    private readonly cookieService: CookieService // private readonly logger: NGXLogger
  ) {
    this.cookiesUtil = new CookiesUtil(cookieService);
    // this.store
    //   .pipe(
    //     select(selectClientIdResult),
    //     tap(clientId => (this.clientId = clientId))
    //   )
    //   .subscribe();

    this.store.pipe(select(selectUserInfoResult)).subscribe((userInfo) => {
      this.authUserInfo = userInfo;
    });

    this.store
      .pipe(
        select(selectAuthoritiesResult),
        filter((value) => Boolean(value))
      )
      .subscribe((authorities) => (this.authorities = authorities));
  }

  // @ts-ignore
  private clientId: ClientIdTypeEnum | null;
  private readonly cookiesUtil: CookiesUtil;
  // private readonly MAXSIZE = env.cookies.maxSizePerCookies;

  public canActivate(route: ActivatedRouteSnapshot): AuthenticationType {
    return this.doAuthenticated(route.data.permissions, route.routeConfig.path);
  }

  public canActivateChild(route: ActivatedRouteSnapshot): AuthenticationType {
    return this.doAuthenticated(route.data.permissions, route.routeConfig.path);
  }

  public checkPermission(permission: Array<string> | string, isRegExPattern = false): boolean {
    const permissions: Array<string> = Array.isArray(permission) ? permission : [permission];
    let hasPermission = false;

    this.store
      .select((state) => (state.userInfo && state.userInfo.authorities ? state.userInfo.authorities : null))
      .subscribe((authorities) => {
        if (permissions && authorities) {
          hasPermission = isRegExPattern
            ? permissions.some((val) => this.checkAuthoritiesRegex(val, authorities))
            : permissions.some((val) => authorities.indexOf(val) > -1);
        }
      });

    return hasPermission;
  }

  generateMenu(config: any[]) {
    let menus = [];

    let filteredByPermission = config.map((parent) => {
      if (!parent.children) {
        return parent;
      }

      const filterChildren = (items) =>
        items.children.map((child) => {
          if (child?.children) {
            const children = filterChildren(child).filter((val) => Boolean(val));

            if (children && children.length) {
              return {
                ...child,
                children
              };
            }
          } else if (child?.data?.permissions) {
            if (this.checkPermission(child.data.permissions, child.data?.isRegex)) {
              return child;
            }
          } else {
            return child;
          }
        });

      return {
        ...parent,
        children: filterChildren(parent).filter((val) => Boolean(val))
      };
    });

    filteredByPermission.forEach((parent) => {
      if (!parent.children) {
        return;
      }

      parent.children.forEach((child) => {
        if (child.data?.isShowOnMenu) {
          let menu = new Menu();
          menu = this.setDefaultMenuSettings(menu, child);

          if (child.children) {
            menu.items = child.children
              .filter((subChild) => subChild.data?.isShowOnMenu)
              .map((subChild) => {
                const submenu = new Menu();
                this.setDefaultMenuSettings(submenu, subChild);
                submenu.badgeClass = ['submenu'];

                if (subChild.path) {
                  submenu.routerLink = [`/${child.path}/${subChild.path}`];
                }
                submenu.featureName = subChild?.data?.featureName;

                return submenu;
              });
          } else if (child.path) {
            menu.routerLink = [`/${child.path}`];
          }

          menus.push(menu);
        }
      });
    });

    return menus;
  }

  setDefaultMenuSettings(menu, item): Menu {
    menu.title = item.data.title;
    menu.icon = item.data?.icon;
    menu.separator = menu.separator;

    return menu;
  }

  public getMenuByPermission(): Menu[] {
    return this.generateMenu(_.cloneDeep(this.router.config));
  }

  private checkAuthoritiesRegex(strReg: string, authorities: string[]): boolean {
    for (const value of authorities) {
      const regex = new RegExp(strReg, 'gi');
      if (regex.exec(value) !== null) {
        return true;
      }
    }
    return false;
  }

  private doAuthenticated(permissions: string[], path: string): boolean {
    const accessTokenCookiesName = this.cookiesUtil.generateNameByAmount(env.cookies.accessToken);
    let canActivate = this.cookieService.check(accessTokenCookiesName[0]);

    if (canActivate && this.authUserInfo.forceChangePassword) {
      canActivate = path === 'change-password';
      if (!canActivate) {
        this.router.navigateByUrl('/change-password', {
          replaceUrl: true
        });
      }
    } else if (canActivate && !this.authUserInfo.forceChangePassword) {
      // authorities => {
      if (path === 'change-password') {
        canActivate = false;
        this.router.navigateByUrl('/main');
      } else if (!permissions) {
        canActivate = true;
      } else {
        if (permissions.some((val) => this.checkAuthoritiesRegex(val, this.authorities))) {
          return canActivate;
        }
        canActivate = false;
        this.router.navigateByUrl('/main');
      }
    } else {
      canActivate = false;
      window.location.href = '/';
    }
    return canActivate;
  }
}
